import { formsService } from '@services/domain-factories/forms';
import { getTableItems } from '@services/domain-factories/tables/tableItems';
import { getTables } from '@services/domain-factories/tables/tableQueries';
import { LinkedDataForForm, tableService } from '@services/domain-factories/tables/tables';
import { Table, TableForm, TableItem } from '@vincerosoft/assessfluence-types';
import { SurveyModel } from 'survey-core';

export async function getLinkedTffTableAndItems(linkedFormId: string) {
  const inFlightStore: {
    linkedTff: TableForm | null;
    linkedTable: Table | null;
    linkedCollectionItems: TableItem[] | null;
  } = {
    linkedTff: null,
    linkedTable: null,
    linkedCollectionItems: null
  };

  inFlightStore.linkedTff = await formsService.getForm<TableForm>('tableLinkedTff', linkedFormId);
  inFlightStore.linkedTable = (await tableService.getTable(inFlightStore.linkedTff)) as Table;

  inFlightStore.linkedCollectionItems = await getTableItems(inFlightStore.linkedTff, {
    table: inFlightStore.linkedTable,
    returnOnlyKeyAndValue: true
  });

  return {
    linkedTff: inFlightStore.linkedTff,
    linkedTable: inFlightStore.linkedTable,
    linkedCollectionItems: inFlightStore.linkedCollectionItems ?? []
  };
}

export async function getDataForLinkedTffIterator(surveyModel: SurveyModel) {
  const allQuestions = surveyModel.getAllQuestions();
  const linkedTffChoices = allQuestions.filter(
    (question) => question.dynamicChoices === 'choicesFromTff' && question.choicesFromTffFormId
  );
  const linkedTffFormIds = new Set<string>(
    linkedTffChoices.map((question) => question.choicesFromTffFormId)
  );

  const ids = Array.from(linkedTffFormIds);
  let linkedData: { [linkedFormId: string]: LinkedDataForForm } = {};

  if (ids.length) {
    const fetchedForms: TableForm[] = [];
    const chunkSize = 10;

    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize);
      const formsChunk = (await formsService.getFormsByIds('tableLinkedTff', chunk)) as TableForm[];
      fetchedForms.push(...formsChunk);
    }

    const tablesByFormId = await getTables(fetchedForms);

    await Promise.all(
      fetchedForms.map(async (linkedTff) => {
        const linkedTable = tablesByFormId.get(linkedTff.id);
        const linkedCollectionItems = linkedTable
          ? await getTableItems(linkedTff, {
              table: linkedTable,
              returnOnlyKeyAndValue: true
            })
          : [];

        if (linkedTable) {
          linkedData = {
            ...linkedData,
            [linkedTff.id]: {
              linkedTff,
              linkedTable,
              linkedCollectionItems: linkedCollectionItems ?? []
            }
          };
        }
      })
    );
  }

  for (const formId of linkedTffFormIds) {
    if (linkedData[formId]?.linkedTff) {
      continue;
    }

    const linkedTffTblData = await getLinkedTffTableAndItems(formId);
    if (!linkedTffTblData.linkedTable) {
      continue;
    }

    linkedData = {
      ...linkedData,
      [formId]: linkedTffTblData as LinkedDataForForm
    };
  }

  return linkedData;
}
